/**
 * Shared formatters for the email tracking dashboard.
 *
 * Two variants exist intentionally — they target different display contexts.
 * Keep them as separate exports so changes to one context cannot silently
 * affect the other (see consolidated review 2026-05-20, HIGH "DRY formatRate").
 */

/**
 * Format an open/click rate (0..1) for the summary KPI cards.
 *
 * Context: hero metric cards in `SummaryCards.tsx`. Designed to surface
 * precision (one decimal place) and to clearly label missing data ("N/A")
 * so admins distinguish "no data" from "0%".
 *
 * @param rate - Ratio in 0..1 range, or null when denominator is zero.
 * @returns e.g. `"42.7%"`, or `"N/A"` when rate is null.
 */
export const formatOpenRateSummary = (rate: number | null): string => {
  if (rate === null) return "N/A";
  return `${(rate * 100).toFixed(1)}%`;
};

/**
 * Format an open/click rate (0..1) for dense tabular rows.
 *
 * Context: table cells in rankings such as `TopUsers.tsx`. Uses no decimal
 * places to keep column width tight and an em-dash to keep the empty state
 * visually quiet alongside other numeric cells.
 *
 * @param rate - Ratio in 0..1 range, or null when denominator is zero.
 * @returns e.g. `"43%"`, or `"—"` when rate is null.
 */
export const formatOpenRateTable = (rate: number | null): string => {
  if (rate === null) return "—";
  return `${(rate * 100).toFixed(0)}%`;
};
